import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SellerNavbar from "../components/SellerNavbar";
import { CATEGORY_OPTIONS } from "../constants/categories";
import { api } from "../services/api";
import "./AddEditProduct.css";

const PRICE_REGEX = /^(?:\d+)(?:\.\d{1,2})?$/;
const DISCOUNT_REGEX = /^(?:100|\d{1,2})(?:\.\d{1,2})?$/;

const createVariant = (name = "", price = "", stock = 0) => ({
  id: crypto.randomUUID(),
  name,
  price,
  stock,
});

const normalizeText = (value) => value.replace(/\s+/g, " ").trim();

const CATEGORY_LOOKUP = new Map(
  CATEGORY_OPTIONS.map((category) => [category.toLowerCase(), category]),
);

const toCanonicalCategory = (value) => {
  const normalized = normalizeText(value).toLowerCase();
  return CATEGORY_LOOKUP.get(normalized) || "";
};

const normalizeLoadedCategories = (product) => {
  if (Array.isArray(product.categories)) {
    return product.categories
      .map((category) => toCanonicalCategory(category))
      .filter(Boolean);
  }

  if (typeof product.category === "string" && product.category.trim()) {
    return [toCanonicalCategory(product.category)].filter(Boolean);
  }

  if (typeof product.categories === "string" && product.categories.trim()) {
    return product.categories
      .split(",")
      .map((category) => toCanonicalCategory(category))
      .filter(Boolean);
  }

  return [];
};

const validateCategories = (categories) =>
  Array.isArray(categories) && categories.length > 0
    ? ""
    : "Select at least one category";

const validateProductField = (field, value) => {
  const text = normalizeText(value);

  if (field === "name") {
    return text ? "" : "Product name is required";
  }

  if (field === "description") {
    return text ? "" : "Product description is required";
  }

  if (field === "categories") {
    return validateCategories(value);
  }

  if (field === "discountRate") {
    const discount = String(value).trim();

    if (!discount) {
      return "Discount rate is required";
    }

    if (!DISCOUNT_REGEX.test(discount)) {
      return "Discount must be between 0 and 100 (e.g. 15 or 15.5)";
    }

    return "";
  }

  return "";
};

const validateVariantField = (field, value) => {
  if (field === "name") {
    return normalizeText(value) ? "" : "Product variant name is required";
  }

  if (field === "price") {
    const price = String(value).trim();

    if (!price) {
      return "Price is required";
    }

    if (!PRICE_REGEX.test(price)) {
      return "Price must be a valid number like 1.59";
    }

    if (Number(price) < 0) {
      return "Price cannot be negative";
    }

    return "";
  }

  if (field === "stock") {
    const stock = String(value).trim();

    if (stock === "") {
      return "";
    }

    if (!/^\d+$/.test(stock)) {
      return "Stock must be a whole number";
    }

    if (Number(stock) < 0) {
      return "Stock cannot be negative";
    }

    return "";
  }

  return "";
};

const getDiscountedPrice = (price, discountRate) => {
  const basePrice = Number(price);
  const rate = Number(discountRate);

  if (!Number.isFinite(basePrice) || basePrice < 0) {
    return null;
  }

  if (!Number.isFinite(rate) || rate < 0) {
    return null;
  }

  return Math.round(basePrice * (1 - Math.min(100, rate) / 100) * 100) / 100;
};

const validateProductForm = (form, variants, imageCount) => {
  const errors = {};
  const variantErrors = {};

  if (!normalizeText(form.name)) {
    errors.name = "Product name is required";
  }

  if (!normalizeText(form.description)) {
    errors.description = "Product description is required";
  }

  if (!Array.isArray(form.categories) || form.categories.length === 0) {
    errors.categories = "Select at least one category";
  }

  if (!String(form.discountRate).trim()) {
    errors.discountRate = "Discount rate is required";
  } else if (!DISCOUNT_REGEX.test(String(form.discountRate).trim())) {
    errors.discountRate =
      "Discount must be between 0 and 100 (e.g. 15 or 15.5)";
  }

  if (imageCount === 0) {
    errors.images = "Please upload at least one product image (max 6)";
  } else if (imageCount > 6) {
    errors.images = "Maximum 6 images allowed";
  }

  variants.forEach((variant) => {
    const currentErrors = {};
    const name = normalizeText(variant.name);
    const price = String(variant.price).trim();

    if (!name) {
      currentErrors.name = "Product variant name is required";
    }

    if (!price) {
      currentErrors.price = "Price is required";
    } else if (!PRICE_REGEX.test(price)) {
      currentErrors.price = "Price must be a valid number like 1.59";
    } else if (Number(price) < 0) {
      currentErrors.price = "Price cannot be negative";
    }

    if (Object.keys(currentErrors).length > 0) {
      variantErrors[variant.id] = currentErrors;
    }
  });

  if (Object.keys(variantErrors).length > 0) {
    errors.variants = variantErrors;
  }

  return errors;
};

export default function AddEditProduct() {
  const [form, setForm] = useState({
    name: "",
    description: "",
    price: "",
    stock: "",
    discountRate: "0",
    categories: [],
  });
  const [variants, setVariants] = useState([createVariant()]);
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");
  const [errors, setErrors] = useState({});
  const [isDragOver, setIsDragOver] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = id ? "Edit Product | Balitah" : "Add Product | Balitah";

    let descriptionTag = document.querySelector('meta[name="description"]');
    if (!descriptionTag) {
      descriptionTag = document.createElement("meta");
      descriptionTag.setAttribute("name", "description");
      document.head.appendChild(descriptionTag);
    }

    descriptionTag.setAttribute(
      "content",
      "Create and update listings on Balitah, a local Bruneian e-commerce platform for sellers.",
    );
  }, [id]);

  useEffect(() => {
    if (!id) {
      return;
    }

    const loadProduct = async () => {
      try {
        const data = await api.getProduct(id);
        const product = data.data || {};

        // Handle images - support both old string format and new object format
        const existingImages = Array.isArray(product.images)
          ? product.images.map((img, index) => {
              if (typeof img === "string") {
                return { url: img, alt: "", order: index };
              }
              return {
                url: img.url,
                alt: img.alt || "",
                order: img.order ?? index,
              };
            })
          : [];

        const existingVariants =
          Array.isArray(product.variants) && product.variants.length > 0
            ? product.variants.map((variant) =>
                createVariant(
                  variant.name || "",
                  variant.price ?? "",
                  variant.stock ?? 0,
                ),
              )
            : [createVariant("", "", 0)];

        setForm({
          name: product.name || "",
          description: product.description || "",
          price: product.price || "",
          stock: product.stock || "",
          discountRate:
            product.discountRate === 0 || product.discountRate
              ? String(product.discountRate)
              : "0",
          categories: normalizeLoadedCategories(product),
        });

        setVariants(existingVariants);

        setImages(
          existingImages.map((img) => ({
            id: `existing-${img.url}`,
            type: "existing",
            ...img,
          })),
        );
      } catch (error) {
        console.error("Failed to load product:", error);
        alert("Failed to load product");
      }
    };

    loadProduct();
  }, [id]);

  const compressImage = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          // Export as square 1:1 image (1200x1200) using center-crop cover.
          const outputSize = 1200;
          canvas.width = outputSize;
          canvas.height = outputSize;

          const scale = Math.max(
            outputSize / img.width,
            outputSize / img.height,
          );
          const drawWidth = Math.round(img.width * scale);
          const drawHeight = Math.round(img.height * scale);
          const offsetX = Math.floor((outputSize - drawWidth) / 2);
          const offsetY = Math.floor((outputSize - drawHeight) / 2);

          ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

          canvas.toBlob(
            (blob) => {
              resolve(new File([blob], file.name, { type: "image/jpeg" }));
            },
            "image/jpeg",
            0.82,
          );
        };

        img.src = e.target.result;
      };

      reader.readAsDataURL(file);
    });

  const addFiles = async (fileList) => {
    const MAX_IMAGES = 6;
    const filesToAdd = Array.from(fileList || []);

    // Check total limit before adding
    const totalWillBe = images.length + filesToAdd.length;
    if (totalWillBe > MAX_IMAGES) {
      alert(
        `Maximum ${MAX_IMAGES} images allowed. You already have ${images.length} image(s). Can only add ${MAX_IMAGES - images.length} more.`,
      );
      return;
    }

    // Compress all new images
    const compressedFiles = await Promise.all(
      filesToAdd.map((file) => compressImage(file)),
    );

    const newImages = compressedFiles.map((file) => ({
      id: `new-${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
      type: "new",
      file,
      url: URL.createObjectURL(file),
    }));

    setImages((current) => [...current, ...newImages]);
  };

  const removeImage = (imageId) => {
    setImages((current) => {
      const removed = current.find((image) => image.id === imageId);

      if (removed?.type === "new") {
        URL.revokeObjectURL(removed.url);
      }

      return current.filter((image) => image.id !== imageId);
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const addVariant = () => {
    setVariants((current) => [...current, createVariant()]);
  };

  const removeVariant = (variantId) => {
    setVariants((current) =>
      current.length > 1
        ? current.filter((variant) => variant.id !== variantId)
        : current,
    );
  };

  const updateVariant = (variantId, field, value) => {
    setVariants((current) =>
      current.map((variant) =>
        variant.id === variantId ? { ...variant, [field]: value } : variant,
      ),
    );

    setErrors((current) => {
      const next = { ...current };
      const fieldError = validateVariantField(field, value);
      const nextVariantErrors = { ...(next.variants?.[variantId] || {}) };

      if (fieldError) {
        nextVariantErrors[field] = fieldError;
      } else {
        delete nextVariantErrors[field];
      }

      if (Object.keys(nextVariantErrors).length === 0) {
        if (next.variants) {
          delete next.variants[variantId];
        }
      } else {
        next.variants = {
          ...(next.variants || {}),
          [variantId]: nextVariantErrors,
        };
      }

      if (next.variants && Object.keys(next.variants).length === 0) {
        delete next.variants;
      }

      return next;
    });
  };

  const handleCategoryToggle = (category) => {
    setForm((current) => {
      const categories = current.categories.includes(category)
        ? current.categories.filter((item) => item !== category)
        : [...current.categories, category];

      setErrors((currentErrors) => {
        if (
          !Object.prototype.hasOwnProperty.call(currentErrors, "categories")
        ) {
          return currentErrors;
        }

        const nextError = validateCategories(categories);

        if (!nextError) {
          const next = { ...currentErrors };
          delete next.categories;
          return next;
        }

        return {
          ...currentErrors,
          categories: nextError,
        };
      });

      return {
        ...current,
        categories,
      };
    });
  };

  const moveImage = (index, direction) => {
    setImages((current) => {
      const nextIndex = index + direction;

      if (nextIndex < 0 || nextIndex >= current.length) {
        return current;
      }

      const next = [...current];
      const [moved] = next.splice(index, 1);
      next.splice(nextIndex, 0, moved);
      return next;
    });
  };

  const uploadImages = async (files) => {
    if (!files.length) {
      return [];
    }

    try {
      const response = await api.uploadImages(files);

      if (
        response &&
        typeof response === "object" &&
        response.success === false
      ) {
        throw new Error(response.message || "Failed to upload images");
      }

      if (Array.isArray(response)) {
        return response;
      }

      if (Array.isArray(response?.data)) {
        return response.data;
      }

      return [];
    } catch (error) {
      console.error("Failed to upload images:", error);
      throw error;
    }
  };

  const MAX_IMAGES = 6;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitting) {
      return;
    }

    const validationErrors = validateProductForm(form, variants, images.length);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    setSubmitStatus("Uploading images...");

    try {
      const newFiles = images
        .filter((image) => image.type === "new")
        .map((image) => image.file);

      let uploadedUrls = [];

      uploadedUrls = await uploadImages(newFiles);

      let uploadedIndex = 0;
      const orderedImages = images
        .map((image, index) => {
          if (image.type === "existing") {
            return {
              url: image.url,
              alt: image.alt || "",
              order: index,
            };
          }

          const url = uploadedUrls[uploadedIndex];
          uploadedIndex += 1;
          return {
            url,
            alt: "",
            order: index,
          };
        })
        .filter((image) => image.url && image.url.trim());

      const normalizedVariants = variants
        .filter(
          (variant) =>
            normalizeText(variant.name) && String(variant.price).trim() !== "",
        )
        .map((variant) => ({
          name: normalizeText(variant.name),
          price: Number(variant.price),
          stock: Number(variant.stock) || 0,
        }))
        .filter(
          (variant) => !Number.isNaN(variant.price) && variant.price >= 0,
        );

      const hasVariants = normalizedVariants.length > 0;
      const fallbackPrice = Number(form.price);
      const fallbackStock = Number(form.stock);
      const derivedPrice = hasVariants
        ? Math.min(...normalizedVariants.map((variant) => variant.price))
        : fallbackPrice;
      const derivedStock = hasVariants
        ? normalizedVariants.reduce(
            (sum, variant) => sum + (Number(variant.stock) || 0),
            0,
          )
        : fallbackStock;

      const payload = {
        ...form,
        name: normalizeText(form.name),
        description: normalizeText(form.description),
        discountRate: Number(form.discountRate),
        categories: form.categories,
        price:
          Number.isFinite(derivedPrice) && derivedPrice >= 0 ? derivedPrice : 0,
        stock:
          Number.isFinite(derivedStock) && derivedStock >= 0 ? derivedStock : 0,
        variants: normalizedVariants,
        images: orderedImages,
      };

      setSubmitStatus(id ? "Updating product..." : "Creating product...");

      if (id) {
        await api.updateProduct(id, payload);
      } else {
        await api.createProduct(payload);
      }

      navigate("/seller");
    } catch (error) {
      console.error("Failed to save product:", error);
      alert(error.message || "Failed to save product");
    } finally {
      setSubmitting(false);
      setSubmitStatus("");
    }
  };

  return (
    <div className="aep-page">
      <SellerNavbar />

      <div className="aep-container">
        {/* Header */}
        <div className="aep-header">
          <button
            type="button"
            className="aep-back-btn"
            onClick={() => navigate(-1)}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="aep-header-content">
            <h1>{id ? "Edit Product" : "Add New Product"}</h1>
            <p>
              {id
                ? "Update your product details"
                : "Create a new product for your store"}
            </p>
          </div>
        </div>

        {submitting && (
          <div className="aep-loading">
            <div className="aep-spinner"></div>
            <span>{submitStatus || "Please wait..."}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="aep-form">
          {/* Images Section */}
          <div className="aep-section">
            <div className="aep-section-header">
              <div className="aep-section-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
              </div>
              <h2 className="aep-section-title">Product Images</h2>
            </div>

            <p className="aep-images-counter">
              {images.length} of {MAX_IMAGES} images uploaded
            </p>
            {images.length > 1 && (
              <p className="aep-images-order-hint">
                Use left and right arrows on each image to set display order
              </p>
            )}

            {/* Upload Zone */}
            <label
              className={`aep-upload-zone ${isDragOver ? "dragover" : ""}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="aep-upload-content">
                <div className="aep-upload-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                </div>
                <p className="aep-upload-text">
                  Click to upload or drag and drop
                </p>
                <p className="aep-upload-hint">PNG, JPG, GIF up to 5MB each</p>
              </div>
              <input
                type="file"
                accept="image/*"
                multiple
                disabled={submitting}
                onChange={(e) => addFiles(e.target.files)}
              />
            </label>

            {errors.images && (
              <div className="aep-error">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="12" r="10" />
                  <text
                    x="12"
                    y="16"
                    textAnchor="middle"
                    fill="white"
                    fontSize="14"
                  >
                    !
                  </text>
                </svg>
                {errors.images}
              </div>
            )}

            {/* Images Grid */}
            {images.length > 0 && (
              <div className="aep-images-grid">
                {images.map((image, index) => (
                  <div key={image.id} className="aep-image-item">
                    <img src={image.url} alt={`Product ${index + 1}`} />
                    <span className="aep-image-order" title="Image position">
                      {index + 1}
                    </span>
                    <div className="aep-image-controls">
                      <button
                        type="button"
                        className="aep-image-move"
                        onClick={() => moveImage(index, -1)}
                        disabled={submitting || index === 0}
                        title="Move left"
                        aria-label={`Move image ${index + 1} left`}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M15 18l-6-6 6-6" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="aep-image-move"
                        onClick={() => moveImage(index, 1)}
                        disabled={submitting || index === images.length - 1}
                        title="Move right"
                        aria-label={`Move image ${index + 1} right`}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M9 18l6-6-6-6" />
                        </svg>
                      </button>
                    </div>
                    <button
                      type="button"
                      className="aep-image-remove"
                      onClick={() => removeImage(image.id)}
                      disabled={submitting}
                      title="Remove image"
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path
                          d="M18 6L6 18M6 6l12 12"
                          strokeWidth="2"
                          stroke="currentColor"
                          fill="none"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Basic Information */}
          <div className="aep-section">
            <div className="aep-section-header">
              <div className="aep-section-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </div>
              <h2 className="aep-section-title">Basic Information</h2>
            </div>

            <div className="aep-form-grid">
              <div className="aep-input-group">
                <label htmlFor="aep-name">
                  Product Name <span className="aep-required">*</span>
                </label>
                <input
                  id="aep-name"
                  type="text"
                  value={form.name}
                  disabled={submitting}
                  placeholder="Enter product name"
                  onChange={(e) => {
                    const value = e.target.value;
                    setForm({ ...form, name: value });
                    setErrors((current) => ({
                      ...current,
                      name: validateProductField("name", value),
                    }));
                  }}
                />
                {errors.name && <div className="aep-error">{errors.name}</div>}
              </div>

              <div className="aep-input-group">
                <label htmlFor="aep-discount">
                  Discount Rate (%) <span className="aep-required">*</span>
                </label>
                <input
                  id="aep-discount"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.discountRate}
                  disabled={submitting}
                  placeholder="0"
                  onChange={(e) => {
                    const value = e.target.value;
                    setForm({ ...form, discountRate: value });
                    setErrors((current) => ({
                      ...current,
                      discountRate: validateProductField("discountRate", value),
                    }));
                  }}
                />
                {errors.discountRate && (
                  <div className="aep-error">{errors.discountRate}</div>
                )}
              </div>
            </div>

            <div className="aep-input-group">
              <label htmlFor="aep-description">
                Description <span className="aep-required">*</span>
              </label>
              <textarea
                id="aep-description"
                value={form.description}
                disabled={submitting}
                placeholder="Describe your product in detail..."
                onChange={(e) => {
                  const value = e.target.value;
                  setForm({ ...form, description: value });
                  setErrors((current) => ({
                    ...current,
                    description: validateProductField("description", value),
                  }));
                }}
              />
              {errors.description && (
                <div className="aep-error">{errors.description}</div>
              )}
            </div>
          </div>

          {/* Categories */}
          <div className="aep-section">
            <div className="aep-section-header">
              <div className="aep-section-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 2H2v10h10V2zM22 12H12v10h10V12zM12 12H2v10h10V12z" />
                </svg>
              </div>
              <h2 className="aep-section-title">Categories</h2>
            </div>

            <div className="aep-categories">
              {CATEGORY_OPTIONS.map((category) => (
                <label key={category} className="aep-category-badge">
                  <input
                    type="checkbox"
                    checked={form.categories.includes(category)}
                    disabled={submitting}
                    onChange={() => handleCategoryToggle(category)}
                    style={{ margin: 0, cursor: "pointer" }}
                  />
                  <span>{category}</span>
                </label>
              ))}
            </div>
            {errors.categories && (
              <div className="aep-error">{errors.categories}</div>
            )}
          </div>

          {/* Product Variants */}
          <div className="aep-section">
            <div className="aep-section-header">
              <div className="aep-section-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M6 4h12v2H6V4zm1 6h10v8H7v-8zm-2 12h14v1H5v-1z" />
                </svg>
              </div>
              <h2 className="aep-section-title">Product Variants</h2>
            </div>

            <div className="aep-variants">
              {variants.map((variant, index) => (
                <div key={variant.id} className="aep-variant-card">
                  <div className="aep-variant-header">
                    <h3 className="aep-variant-title">Variant {index + 1}</h3>
                    {variants.length > 1 && (
                      <button
                        type="button"
                        className="aep-variant-remove"
                        onClick={() => removeVariant(variant.id)}
                        disabled={submitting}
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="aep-variant-form">
                    <div className="aep-input-group">
                      <label htmlFor={`var-name-${variant.id}`}>
                        Variant Name
                      </label>
                      <input
                        id={`var-name-${variant.id}`}
                        type="text"
                        value={variant.name}
                        disabled={submitting}
                        placeholder="e.g., Large, Red"
                        onChange={(e) =>
                          updateVariant(variant.id, "name", e.target.value)
                        }
                      />
                      {errors.variants?.[variant.id]?.name && (
                        <div className="aep-error">
                          {errors.variants[variant.id].name}
                        </div>
                      )}
                    </div>

                    <div className="aep-input-group">
                      <label htmlFor={`var-price-${variant.id}`}>
                        Price (BND)
                      </label>
                      <input
                        id={`var-price-${variant.id}`}
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        value={variant.price}
                        disabled={submitting}
                        placeholder="0.00"
                        onChange={(e) =>
                          updateVariant(variant.id, "price", e.target.value)
                        }
                      />
                      {errors.variants?.[variant.id]?.price && (
                        <div className="aep-error">
                          {errors.variants[variant.id].price}
                        </div>
                      )}
                    </div>

                    <div className="aep-input-group">
                      <label htmlFor={`var-stock-${variant.id}`}>Stock</label>
                      <input
                        id={`var-stock-${variant.id}`}
                        type="number"
                        inputMode="numeric"
                        min="0"
                        value={variant.stock}
                        disabled={submitting}
                        placeholder="0"
                        onChange={(e) =>
                          updateVariant(variant.id, "stock", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  {/* Discount Preview */}
                  {String(variant.price).trim() && (
                    <div
                      style={{
                        marginTop: "12px",
                        padding: "12px",
                        background: "#f3f4f6",
                        borderRadius: "8px",
                        fontSize: "14px",
                        color: "#6b7280",
                      }}
                    >
                      <span style={{ fontWeight: 500 }}>Discounted Price:</span>{" "}
                      <span style={{ color: "#e94560", fontWeight: 600 }}>
                        BND{" "}
                        {(
                          getDiscountedPrice(
                            variant.price,
                            form.discountRate,
                          ) ?? 0
                        ).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <button
              type="button"
              className="aep-btn aep-btn-secondary aep-btn-small"
              onClick={addVariant}
              disabled={submitting}
              style={{ marginTop: "16px" }}
            >
              + Add Variant
            </button>
          </div>

          {/* Action Buttons */}
          <div className="aep-actions">
            <button
              type="submit"
              className="aep-btn aep-btn-primary"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <div className="aep-spinner"></div>
                  {id ? "Updating..." : "Creating..."}
                </>
              ) : (
                <>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  </svg>
                  {id ? "Update Product" : "Create Product"}
                </>
              )}
            </button>

            <button
              type="button"
              className="aep-btn aep-btn-secondary"
              onClick={() => navigate(-1)}
              disabled={submitting}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
